import { Fragment, useContext } from 'react'
import NextLink from 'next/link'

import { Link as MuiLink } from '@mui/material'

import { SanityIcon } from '@/components/SanityIcon'
import { getTarget } from '@/helpers/getTarget'
import { getHighlightedLinkStyle, toLinkColors } from '@/helpers/linkColors'
import { useSiteSettings } from '@/hooks/useSiteSettings'
import { LanguageContext } from '@/providers/TranslationContextProvider'

import type React from 'react'
import type { FC } from 'react'
import type { LinkProps as MuiLinkProps } from '@mui/material'
import type { SanityLink } from '@/interfaces/LayoutSettings'

type LinkProps = Partial<SanityLink> & {
  linkProps?: MuiLinkProps
}

interface IconProps {
  name?: string
}

const Icon: FC<IconProps> = ({ name }) => {
  if (name) {
    return <SanityIcon name={name} sx={{ mr: 1, fontSize: '0.875rem' }} />
  } else {
    return null
  }
}

export const Link: React.FC<LinkProps> = (props) => {
  const { language } = useContext(LanguageContext)
  const { getLinkByName } = useSiteSettings()
  const linkFromSettings = getLinkByName(props.internalName)

  const externalName = props.externalName ?? linkFromSettings?.externalName
  const openInNewTab = props.openInNewTab ?? linkFromSettings?.openInNewTab
  const url = props.url ?? linkFromSettings?.url
  const noUrl = props.noUrl ?? linkFromSettings?.noUrl
  const iconImage = props.iconImage ?? linkFromSettings?.iconImage
  const linkProps = props.linkProps
  const id = props._id ?? linkFromSettings?._id
  const colors = toLinkColors({
    backgroundColor: props.backgroundColor ?? linkFromSettings?.backgroundColor,
    textColor: props.textColor ?? linkFromSettings?.textColor,
  })
  const label = externalName?.[language]

  const { rel, target } = getTarget(openInNewTab)

  if (!label) {
    return null
  }

  if (noUrl) {
    return (
      <Fragment key={id}>
        <Icon name={iconImage?.name} />
        {label}
      </Fragment>
    )
  }

  if (!url) {
    return null
  }

  return (
    <MuiLink
      component={NextLink}
      key={id}
      color="secondary"
      href={url}
      sx={{ display: 'inline-flex', alignItems: 'center' }}
      rel={rel}
      target={target}
      style={getHighlightedLinkStyle(colors)}
      {...linkProps}
    >
      <Icon name={iconImage?.name} />
      {label}
    </MuiLink>
  )
}
